<script lang="ts">
  import { onMount } from 'svelte';
  import { createForm, formListStore } from '@entities/form/store.svelte';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import { navigate } from '@shared/router/navigation';
  import { canEditMasters } from '@shared/types/session';
  import type { Form, FormStatus, JsonObject } from '@shared/types/form';
  import { formatDateTime } from '@shared/util/format';

  let createOpen = $state(false);
  let saving = $state(false);
  let createError = $state<string | null>(null);
  let createDraft = $state({
    slug: '',
    name: '',
    form_type: 'contact',
    settingsText: '{}',
  });

  let allowEdit = $derived(sessionStore.user ? canEditMasters(sessionStore.user.role) : false);

  function parseJsonObject(raw: string, label: string): JsonObject {
    const text = raw.trim() || '{}';
    const value = JSON.parse(text) as unknown;
    if (value === null || Array.isArray(value) || typeof value !== 'object') {
      throw new Error(`${label} は JSON object で入力してください`);
    }
    return value as JsonObject;
  }

  function statusLabel(status: FormStatus): string {
    if (status === 'draft') return '下書き';
    if (status === 'published') return '公開中';
    return 'アーカイブ';
  }

  function statusTagClass(status: FormStatus): string {
    if (status === 'published') return 'tag tag-ok';
    if (status === 'archived') return 'tag tag-danger';
    return 'tag tag-warn';
  }

  function formPath(form: Form): string {
    return `/forms/${form.id}`;
  }

  function handleLinkClick(ev: MouseEvent, path: string): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
    ev.preventDefault();
    navigate(path);
  }

  function resetCreateDraft(): void {
    createDraft = {
      slug: '',
      name: '',
      form_type: 'contact',
      settingsText: '{}',
    };
    createError = null;
  }

  async function handleCreate(): Promise<void> {
    if (!allowEdit || saving) return;
    saving = true;
    createError = null;
    try {
      const form = await createForm({
        slug: createDraft.slug.trim(),
        name: createDraft.name.trim(),
        form_type: createDraft.form_type.trim(),
        settings: parseJsonObject(createDraft.settingsText, 'settings'),
      });
      createOpen = false;
      resetCreateDraft();
      navigate(`/forms/${form.id}`);
    } catch (err) {
      createError = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  onMount(() => {
    void formListStore.refresh();
  });
</script>

<header class="page-head">
  <div>
    <h1>フォーム管理</h1>
  </div>
  <button
    type="button"
    class="btn btn-primary"
    disabled={!allowEdit}
    title={allowEdit ? '' : '権限がありません'}
    onclick={() => {
      resetCreateDraft();
      createOpen = true;
    }}
  >
    + フォームを作成
  </button>
</header>

{#if createOpen}
  <section class="panel create-panel" aria-label="フォーム作成">
    <form
      onsubmit={(ev) => {
        ev.preventDefault();
        void handleCreate();
      }}
    >
      <div class="panel-header">
        <h2>新規フォーム</h2>
        <button
          type="button"
          class="btn"
          disabled={saving}
          onclick={() => (createOpen = false)}
        >
          閉じる
        </button>
      </div>
      {#if createError}
        <p class="error" role="alert">{createError}</p>
      {/if}
      <div class="form-grid">
        <label>
          <span>slug</span>
          <input
            bind:value={createDraft.slug}
            maxlength="120"
            required
            disabled={saving}
            autocomplete="off"
          />
        </label>
        <label>
          <span>名称</span>
          <input
            bind:value={createDraft.name}
            maxlength="200"
            required
            disabled={saving}
            autocomplete="off"
          />
        </label>
        <label>
          <span>種別</span>
          <input
            bind:value={createDraft.form_type}
            maxlength="80"
            required
            disabled={saving}
            autocomplete="off"
          />
        </label>
        <label class="wide">
          <span>settings JSON</span>
          <textarea bind:value={createDraft.settingsText} rows="5" disabled={saving}></textarea>
        </label>
      </div>
      <div class="actions">
        <button type="submit" class="btn btn-primary" disabled={saving}>
          {saving ? '保存中...' : '作成'}
        </button>
      </div>
    </form>
  </section>
{/if}

<section class="panel">
  {#if formListStore.loading && formListStore.data === null}
    <p class="muted">読み込み中...</p>
  {:else if formListStore.error}
    <p class="error" role="alert">{formListStore.error}</p>
  {:else if formListStore.data && formListStore.data.length > 0}
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>名称</th>
            <th>ステータス</th>
            <th>種別</th>
            <th>slug</th>
            <th>schema</th>
            <th>更新日時</th>
          </tr>
        </thead>
        <tbody>
          {#each formListStore.data as form (form.id)}
            <tr>
              <td class="name-cell">
                <a
                  href={formPath(form)}
                  onclick={(ev) => handleLinkClick(ev, formPath(form))}
                >
                  {form.name}
                </a>
                <div class="row-sub">{form.id}</div>
              </td>
              <td><span class={statusTagClass(form.status)}>{statusLabel(form.status)}</span></td>
              <td>{form.form_type}</td>
              <td><code>{form.slug}</code></td>
              <td>{form.schema_version}</td>
              <td class="timestamp">{formatDateTime(form.updated_at)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {:else}
    <p class="empty">フォームはまだ登録されていません。</p>
  {/if}
</section>

<style>
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;
    flex-wrap: wrap;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .page-head .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }
  .panel-header h2 {
    margin: 0;
    font-size: 1rem;
  }
  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(180px, 1fr));
    gap: 12px;
  }
  .form-grid label {
    display: grid;
    gap: 4px;
  }
  .form-grid label span {
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .form-grid .wide {
    grid-column: 1 / -1;
  }
  .form-grid input,
  .form-grid textarea {
    width: 100%;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }
  .name-cell {
    min-width: 240px;
  }
  .row-sub {
    margin-top: 2px;
    color: var(--muted);
    font-size: 11px;
  }
  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  .timestamp {
    white-space: nowrap;
  }
  @media (max-width: 820px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
